import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import type { LeoDemandeResponse, LeoInstantaneResponse, LeoPrecedent } from "@shared/types";
import { ApiError } from "../../lib/api";
import { Voix, debloquerAudio, demanderFlux, ecouteServeurDisponible, ecouterNiveauMicro, heureCourte, impulsionMicro, phrasesCompletes, startEcouteServeur, startRecognition, supportsEcouteServeur, supportsRecognition, supportsSpeech, type Dictee } from "../../lib/leo";
import { useAuthStore } from "../../stores/auth";

interface Message {
  id: number;
  role: "me" | "leo";
  texte: string;
  lignes?: string[];
  question?: boolean;
  modele?: boolean;
  inconnu?: boolean;
  erreur?: boolean;
  /** Réponse encore en cours de diffusion. */
  enCours?: boolean;
}

type Etat = "repos" | "ecoute" | "reflexion" | "parle";

const CARTES: Array<{ cle: "appels" | "rdv" | "rappels" | "enCours"; label: string; question: string }> = [
  { cle: "appels", label: "Appels aujourd’hui", question: "Les appels d’aujourd’hui" },
  { cle: "rdv", label: "Rendez-vous pris", question: "Les rendez-vous pris aujourd’hui" },
  { cle: "rappels", label: "À rappeler", question: "Qui je dois rappeler ?" },
  { cle: "enCours", label: "En cours", question: "Appels en cours ?" },
];

const ETIQUETTE: Record<Etat, string> = { repos: "", ecoute: "je t’écoute", reflexion: "un instant", parle: "" };
const ETIQUETTE_SCENE: Record<Etat, string> = { repos: "prêt", ecoute: "je t’écoute", reflexion: "je réfléchis", parle: "je parle" };
// Ce que le navigateur dit de la dictée quand elle n'entend rien, en clair sous l'orbe.
const NOTES_DICTEE: Record<string, string> = {
  "no-speech": "je n’ai rien entendu",
  network: "dictée sans réseau",
  "audio-capture": "micro introuvable",
  "language-not-supported": "français non disponible pour la dictée",
  transcription: "transcription indisponible",
};
const BARRES = 28;
const MAX_SILENCES = 2;
// Après la voix de Léo, on laisse le téléphone refermer la session audio de lecture avant d'écouter.
const DELAI_REECOUTE_MS = 600;

let seq = 0;

interface LeoChatProps {
  snap: LeoInstantaneResponse | null;
  snapErreur: string | null;
  onRefresh: () => void;
  onClose: () => void;
}

/**
 * Le panneau de Léo : accueil avec les chiffres du jour, conversation (texte ou
 * voix), réponses diffusées au fil de l'eau et lues phrase par phrase, mode
 * conversation mains libres (Léo réécoute après avoir parlé).
 */
export function LeoChat({ snap, snapErreur, onRefresh, onClose }: LeoChatProps) {
  const user = useAuthStore((s) => s.user);
  const [messages, setMessages] = useState<Message[]>([]);
  const [saisie, setSaisie] = useState("");
  const [busy, setBusy] = useState(false);
  const [etat, setEtat] = useState<Etat>("repos");
  const [niveau, setNiveau] = useState(0);
  const [micAbsent, setMicAbsent] = useState(!supportsRecognition() && !supportsEcouteServeur());
  const [mainsLibres, setMainsLibres] = useState(false);
  const [noteDictee, setNoteDictee] = useState("");
  const [accueilPart, setAccueilPart] = useState(false);
  // Léo parle par défaut ; « muet » est mémorisé dans le navigateur (clé leo-voix).
  const [voixActive, setVoixActive] = useState<boolean>(() => {
    try {
      return localStorage.getItem("leo-voix") !== "0";
    } catch {
      return true;
    }
  });

  const contexte = useRef<{ precedent: LeoPrecedent | null; enAttente: string | null; historique: Array<{ role: "user" | "assistant"; content: string }> }>({ precedent: null, enAttente: null, historique: [] });
  const dictee = useRef<Dictee | null>(null);
  const arretMicro = useRef<(() => void) | null>(null);
  const silences = useRef(0);
  const refs = useRef({ voixActive, mainsLibres, busy, ecoute: false, ecouteServeur: false });
  refs.current = { ...refs.current, voixActive, mainsLibres, busy, ecouteServeur: !!snap?.ecoute };
  const chatRef = useRef<HTMLDivElement>(null);
  const saisieRef = useRef<HTMLInputElement>(null);
  const ecouterRef = useRef<() => void>(() => undefined);

  const voix = useMemo(
    () =>
      new Voix({
        onEtat: (parle) => {
          setEtat((e) => (parle ? "parle" : e === "parle" ? "repos" : e));
          // Mains libres : Léo a fini de parler, il réécoute.
          if (!parle && refs.current.mainsLibres && !refs.current.busy) window.setTimeout(() => ecouterRef.current(), DELAI_REECOUTE_MS);
        },
        onNiveau: setNiveau,
      }),
    [],
  );

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, mainsLibres]);

  // Pas de focus automatique à l'ouverture : sur mobile, ça ouvrait le clavier.
  useEffect(() => {
    return () => {
      voix.couper();
      dictee.current?.abort();
      arretMicro.current?.();
    };
  }, [voix]);

  const barres = useMemo(() => Array.from({ length: BARRES }, (_, i) => ({ k: 0.35 + ((i * 7919) % 100) / 150, d: (i * 37) % 900 })), []);

  const ajouterLeo = useCallback((m: Omit<Message, "id" | "role">) => {
    const id = ++seq;
    setMessages((ms) => [...ms, { id, role: "leo", ...m }]);
    return id;
  }, []);
  const modifierLeo = useCallback((id: number, patch: Partial<Message>) => {
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  const poser = useCallback(
    async (texte: string, dictee: boolean) => {
      const q = texte.trim();
      if (!q || refs.current.busy) return;
      setBusy(true);
      refs.current.busy = true;
      setSaisie("");
      voix.couper();
      debloquerAudio();
      setAccueilPart(true);
      setEtat("reflexion");
      const ctx = contexte.current;
      setMessages((m) => [...m, { id: ++seq, role: "me", texte: q }]);
      const lire = refs.current.voixActive || dictee;
      let idLeo: number | null = null;
      let flux = "";
      let dejaLu = "";
      try {
        await demanderFlux(
          { texte: q, enAttente: ctx.enAttente, precedent: ctx.precedent, historique: ctx.historique },
          {
            onDebut: () => {
              idLeo = ajouterLeo({ texte: "", enCours: true, modele: true });
            },
            onDelta: (delta) => {
              flux += delta;
              if (idLeo !== null) modifierLeo(idLeo, { texte: flux });
              if (lire) {
                const { pretes, reste } = phrasesCompletes(flux.slice(dejaLu.length));
                for (const p of pretes) voix.dire(p);
                dejaLu = flux.slice(0, flux.length - reste.length);
              }
            },
            onFin: (res: LeoDemandeResponse) => {
              ctx.historique = [...ctx.historique, { role: "user" as const, content: q }, { role: "assistant" as const, content: res.texte }].slice(-10);
              ctx.enAttente = res.enAttente;
              if (res.famille) ctx.precedent = { famille: res.famille, mode: res.mode ?? undefined, slots: res.slots, detail: res.detail ?? ctx.precedent?.detail ?? null };
              else if (res.type === "question") ctx.precedent = { ...(ctx.precedent ?? { famille: null }), slots: res.slots };
              const contenu = { texte: res.texte, lignes: res.lignes, question: res.type === "question", modele: res.modele, inconnu: res.inconnu, enCours: false };
              if (idLeo !== null) modifierLeo(idLeo, contenu);
              else ajouterLeo(contenu);
              if (lire) {
                if (idLeo !== null) {
                  const reste = res.texte.slice(dejaLu.length).trim();
                  if (reste) voix.dire(reste);
                } else voix.dire(res.parole || res.texte);
              }
            },
          },
        );
      } catch (err) {
        const texteErreur = err instanceof ApiError && err.status === 502 ? "Une ligne ne répond pas pour le moment. Réessaie dans une minute." : "Je n’arrive pas à joindre le serveur. Réessaie dans un instant.";
        if (idLeo !== null) modifierLeo(idLeo, { texte: texteErreur, erreur: true, enCours: false });
        else ajouterLeo({ texte: texteErreur, erreur: true });
      } finally {
        setBusy(false);
        refs.current.busy = false;
        setEtat((e) => (e === "reflexion" ? "repos" : e));
        // Après un message tapé, on rend le focus au champ sur PC seulement (sur mobile, ça rouvrirait le clavier).
        if (!dictee && window.matchMedia?.("(pointer: fine)").matches) saisieRef.current?.focus();
        // Rien à lire (muet, ou réponse vide) : en mains libres, on réécoute tout de suite.
        if (!voix.parle && refs.current.mainsLibres) window.setTimeout(() => ecouterRef.current(), DELAI_REECOUTE_MS);
      }
    },
    [ajouterLeo, modifierLeo, voix],
  );

  // `auto` = réécoute lancée par Léo lui-même (mains libres), pas par un geste :
  // un refus à ce moment-là arrête la conversation sans déclarer le micro absent.
  // Un geste, lui, redémarre toujours : la dictée en cours est abandonnée.
  const ecouter = useCallback((auto = false) => {
    if (refs.current.busy) return;
    if (auto && refs.current.ecoute) return;
    dictee.current?.abort();
    dictee.current = null;
    debloquerAudio();
    voix.couper();
    let erreur = "";
    // Écoute par le serveur (micro enregistré, transcription ElevenLabs) quand il
    // l'offre et que le navigateur sait enregistrer ; sinon dictée du navigateur.
    const serveur = refs.current.ecouteServeur && ecouteServeurDisponible();
    const demarrer = serveur ? startEcouteServeur : startRecognition;
    const d = demarrer({
      onStart: () => {
        setEtat("ecoute");
        setNoteDictee("");
        refs.current.ecoute = true;
        if (!serveur) arretMicro.current = ecouterNiveauMicro(setNiveau);
      },
      onNiveau: setNiveau,
      onTranscription: () => setEtat("reflexion"),
      onError: (code) => {
        erreur = code;
        console.warn("[Léo] écoute :", code);
      },
      onInterim: (t) => {
        setSaisie(t);
        impulsionMicro();
      },
      onFinal: (t) => {
        silences.current = 0;
        void poser(t, true);
      },
      onEnd: (silence, abandon) => {
        refs.current.ecoute = false;
        arretMicro.current?.();
        arretMicro.current = null;
        if (dictee.current === d) dictee.current = null;
        // La transcription a pu passer l'orbe en « réflexion » : sans texte, retour au repos.
        setEtat((e) => (e === "ecoute" || (silence && e === "reflexion" && !refs.current.busy) ? "repos" : e));
        if (silence && !abandon) setNoteDictee(NOTES_DICTEE[erreur] ?? NOTES_DICTEE["no-speech"]!);
        // Un abandon (arrêt par l'équipe, redémarrage) n'est pas un silence.
        if (silence && !abandon && refs.current.mainsLibres) {
          silences.current += 1;
          if (silences.current >= MAX_SILENCES) {
            setMainsLibres(false);
            refs.current.mainsLibres = false;
            silences.current = 0;
          } else window.setTimeout(() => ecouterRef.current(), 250);
        }
      },
      onDenied: () => {
        if (!auto) setMicAbsent(true);
        setMainsLibres(false);
        refs.current.mainsLibres = false;
      },
    });
    if (!d) {
      if (!auto && !(serveur ? supportsRecognition() : false)) setMicAbsent(true);
      setMainsLibres(false);
      refs.current.mainsLibres = false;
    }
    dictee.current = d;
  }, [poser, voix]);
  ecouterRef.current = () => ecouter(true);

  const basculerVoix = (forcer?: boolean) => {
    const next = typeof forcer === "boolean" ? forcer : !voixActive;
    setVoixActive(next);
    refs.current.voixActive = next;
    try {
      localStorage.setItem("leo-voix", next ? "1" : "0");
    } catch {
      // stockage indisponible
    }
    if (next) debloquerAudio();
    else voix.couper();
  };

  const basculerMainsLibres = () => {
    const next = !mainsLibres;
    setMainsLibres(next);
    refs.current.mainsLibres = next;
    silences.current = 0;
    if (next) {
      if (!voixActive) basculerVoix(true);
      ecouter();
    } else {
      // Tout s'arrête net : la dictée (rien n'est envoyé) et la voix de Léo.
      dictee.current?.abort();
      dictee.current = null;
      voix.couper();
      setSaisie("");
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void poser(saisie, false);
  };

  const k = snap?.kpis;
  const lignesHs = snap ? snap.kpis.lignes - snap.kpis.lignesOk : 0;
  const statut = ETIQUETTE[etat];

  return (
    <div className={`leo-chat is-${etat}`} style={{ "--lvl": niveau } as CSSProperties}>
      <header className="leo-chat__head">
        <span className="leo-chat__mark" aria-hidden="true">
          <img src="/logo_mark.png" alt="" width="22" height="22" />
        </span>
        <div className="leo-chat__titles">
          <b>Léo</b>
          <span className="leo-chat__status">
            {snap ? (
              <>
                <i className={`leo-chat__dot${lignesHs ? " is-bad" : ""}`} />
                {statut || `${snap.kpis.lignesOk}/${snap.kpis.lignes} ${snap.kpis.lignes > 1 ? "lignes" : "ligne"}`}
                {etat === "repos" ? (
                  <>
                    {" · "}
                    <button type="button" className="leo-chat__link" onClick={onRefresh} title="Actualiser les données">
                      {heureCourte(snap.genereLe)}
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <i className={`leo-chat__dot${snapErreur ? " is-bad" : " is-off"}`} />
                {snapErreur ?? "chargement…"}
              </>
            )}
          </span>
        </div>
        {supportsSpeech() ? (
          <button type="button" className={`leo-ic${voixActive ? " is-on" : ""}`} onClick={() => basculerVoix()} aria-pressed={voixActive} title={voixActive ? "Léo parle (mettre en sourdine)" : "Léo est en sourdine (activer la voix)"}>
            {voixActive ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11 5 6 9H2v6h4l5 4z" />
                <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                <path d="M18.5 5.5a9 9 0 0 1 0 13" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11 5 6 9H2v6h4l5 4z" />
                <path d="m22 9-6 6M16 9l6 6" />
              </svg>
            )}
          </button>
        ) : null}
        <button type="button" className="leo-ic" onClick={onClose} aria-label="Fermer Léo" title="Fermer">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </header>

      {mainsLibres ? (
        <div className="leo-scene" aria-live="polite">
          <div className="leo-orb-wrap">
            <div className={`leo-orb is-${etat}`}>
              <i className="leo-orb__ticks" />
              <i className="leo-orb__hud" />
              <i className="leo-orb__glow" />
              <i className="leo-orb__ring" />
              <span className="leo-orb__sphere">
                <i className="leo-orb__aurora leo-orb__aurora--a" />
                <i className="leo-orb__aurora leo-orb__aurora--b" />
                <i className="leo-orb__aurora leo-orb__aurora--c" />
                <i className="leo-orb__grain" />
                <i className="leo-orb__spec" />
              </span>
              <i className="leo-orb__rim" />
              <i className="leo-orb__orbit">
                <b />
                <b />
                <b />
              </i>
            </div>
          </div>
          <p className="leo-scene__label">{ETIQUETTE_SCENE[etat]}</p>
          {/* Ce que Léo entend, au fil des mots ; la phrase finale rejoint la conversation dessous. */}
          <p className={`leo-scene__you${etat !== "ecoute" && noteDictee ? " is-note" : ""}`}>{etat === "ecoute" ? saisie || "\u00a0" : noteDictee || "\u00a0"}</p>
        </div>
      ) : null}

      <div className="leo-wave" aria-hidden="true" hidden={mainsLibres}>
        {barres.map((b, i) => (
          <i key={i} style={{ "--k": b.k, "--d": `${b.d}ms` } as CSSProperties} />
        ))}
      </div>

      <div className="leo-chat__log" ref={chatRef} aria-live="polite">
        {(messages.length === 0 && !mainsLibres) || accueilPart ? (
          <div className={`leo-welcome${accueilPart ? " is-out" : ""}`} aria-hidden={accueilPart} onAnimationEnd={() => accueilPart && setAccueilPart(false)}>
            <p className="leo-welcome__hello">
              <b>Salut{user?.name ? ` ${user.name.split(" ")[0]}` : ""}.</b> {snap?.admin ? "Nos lignes et les clients supervisés, en direct." : "Ton établissement, en direct."}
            </p>
            <div className="leo-cards" aria-label="Chiffres du jour">
              {CARTES.map((c, i) => (
                <button key={c.cle} type="button" className={`leo-card${c.cle === "enCours" && k && k.enCours > 0 ? " is-live" : ""}`} style={{ animationDelay: `${60 + i * 50}ms` }} onClick={() => void poser(c.question, false)} disabled={busy}>
                  <b>{k ? String(k[c.cle]) : "–"}</b>
                  <span>{c.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((m) => (
          <div key={m.id} className={`leo-msg leo-msg--${m.role}${m.erreur ? " is-error" : ""}${m.question ? " is-question" : ""}${m.enCours ? " is-streaming" : ""}`}>
            <div className="leo-msg__bubble">
              <p>
                {m.texte}
                {m.enCours ? <span className="leo-msg__cursor" /> : null}
              </p>
              {m.lignes && m.lignes.length ? (
                <ul>
                  {m.lignes.map((l, i) => (
                    <li key={i} className={/^⚠/.test(l) ? "is-warn" : /^Léo : /.test(l) ? "is-ia" : /^Client : /.test(l) ? "is-client" : undefined}>
                      {l}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ))}
        {busy && !messages.some((m) => m.enCours) ? (
          <div className="leo-msg leo-msg--leo leo-msg--typing">
            <div className="leo-msg__bubble">
              <i />
              <i />
              <i />
            </div>
          </div>
        ) : null}
      </div>

      {mainsLibres ? (
        <div className="leo-chat__bar leo-chat__bar--scene">
          <button type="button" className="leo-scene__stop" onClick={basculerMainsLibres}>
            Arrêter la conversation
          </button>
        </div>
      ) : (
      <form className="leo-chat__bar" onSubmit={onSubmit} autoComplete="off">
        <input
          ref={saisieRef}
          type="text"
          className="leo-chat__input"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Demande-moi…"
          maxLength={500}
          enterKeyHint="send"
          aria-label="Question à Léo"
        />
        {saisie.trim() ? (
          <button type="submit" className="leo-btn" disabled={busy} aria-label="Envoyer">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h13" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </button>
        ) : (
          // Champ vide : la touche lance la conversation vocale (orbe).
          <button type="button" className="leo-btn" onClick={basculerMainsLibres} disabled={busy || micAbsent} aria-label="Parler avec Léo" title={micAbsent ? "Dictée vocale indisponible sur ce navigateur" : "Parler avec Léo"}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />
            </svg>
          </button>
        )}
      </form>
      )}
    </div>
  );
}
